import { Badge, Button, Group, Loader, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";

interface WebhookDelivery {
  id: string;
  event: string;
  status: string;
  attempts: number;
  lastAttemptAt: string;
  workspaceId: string | null;
}

const statusColor = (status: string) => {
  if (status === "delivered") return "teal";
  if (status === "failed") return "red";
  if (status === "pending") return "blue";
  return "gray";
};

export const WebhooksPage = () => {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["internal", "webhooks"],
    queryFn: () => api.get<{ deliveries: WebhookDelivery[] }>("/internal/webhooks")
  });

  const replay = useMutation({
    mutationFn: (deliveryId: string) => api.post("/internal/webhooks/replay", { deliveryId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["internal", "webhooks"] });
    }
  });

  const deliveries = data?.deliveries ?? [];
  const failed = deliveries.filter((d) => d.status === "failed");

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Webhooks</Title>
        {failed.length > 0 && (
          <Badge color="red" size="sm">
            {failed.length} failed
          </Badge>
        )}
      </Group>

      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        {isLoading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Event</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Attempts</Table.Th>
                <Table.Th>Last attempt</Table.Th>
                <Table.Th>Workspace</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {deliveries.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" p="md">
                      No webhook deliveries
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                deliveries.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {d.event}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={statusColor(d.status)}>
                        {d.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{d.attempts}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {new Date(d.lastAttemptAt).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {d.workspaceId ?? "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {d.status === "failed" && (
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconRefresh size={12} />}
                          loading={replay.isPending}
                          onClick={() => replay.mutate(d.id)}
                        >
                          Replay
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
};
